import { t } from "i18next";
import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import { BOARD_SHARE_LINK_ACCESS_PASSWORD_APPLY_V2 } from "@/api/apiEndpoints";
import { canv } from "@/canv/global";
import { MakeRequest } from "@/canv/utils";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { showSnackbar } from "@/snackbar/snackbar";
import styles from "./PasswordWindow.module.css";
import { PasswordWindowLayout } from "./PasswordWindowLayout";

type SuccessCallback = (token: string) => void;

export class PasswordWindow {
    async open(success: SuccessCallback) {
        this.success = success;

        const passwordWindowWrapper = document.createElement("div");
        passwordWindowWrapper.classList.add(styles.passwordWindowWrapper);

        this.passwordWindowWrapper = passwordWindowWrapper;
        document.body.prepend(passwordWindowWrapper);

        this.root = createRoot(passwordWindowWrapper);

        this.render();
    }

    render() {
        const PasswordWindowLayoutElement = createElement(PasswordWindowLayout, {
            onAction: this.actionClick,
        });

        if (this.root) {
            this.root.render(PasswordWindowLayoutElement);
        } else {
            throw new Error("PasswordWindow can't find this.root");
        }
    }

    close() {
        this.root?.unmount();

        if (this.passwordWindowWrapper?.parentNode) {
            this.passwordWindowWrapper.parentNode.removeChild(this.passwordWindowWrapper);
            this.passwordWindowWrapper = null;
        }
    }

    actionClick = async (password: string) => {
        if (password === "") {
            showSnackbar(t("passwordWindow.emptyError"), {
                type: "warning",
                duration: SNACKBAR_TIME,
            });
            return;
        }

        const passToToken = await MakeRequest(BOARD_SHARE_LINK_ACCESS_PASSWORD_APPLY_V2, {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
            password: password,
        });

        if (passToToken.status !== "success") {
            showSnackbar(t("passwordWindow.wrongError"), {
                type: "error",
                duration: SNACKBAR_TIME,
            });
            return;
        }

        this.close();
        this.success?.(passToToken.boardAccessToken);
    };

    root: Root | null = null;
    success: SuccessCallback | null = null;
    passwordWindowWrapper: HTMLElement | null = null;
}
